import { z } from 'zod';

// src/spacing.ts
var SPACING_SIDES = ["top", "right", "bottom", "left"];
var DEFAULT_MARGIN = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "auto" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "auto" }
};
var DEFAULT_PADDING = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "px" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "px" }
};
var ZERO_MARGIN = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "px" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "px" }
};
var DEFAULT_BASE_SPACING = {
  margin: DEFAULT_MARGIN,
  padding: DEFAULT_PADDING,
  width: { value: 0, unit: "auto" },
  maxWidth: { value: 100, unit: "%" }
};
var DEFAULT_SLOT_SPACING = {
  margin: ZERO_MARGIN,
  padding: DEFAULT_PADDING,
  width: { value: 0, unit: "auto" },
  maxWidth: { value: 0, unit: "none" }
};
var MAX_LENGTH_MAGNITUDE = 1e4;
function cssLength(side, allowAuto) {
  if (!side) return "0px";
  if (allowAuto && side.unit === "auto") return "auto";
  const unit = side.unit === "%" ? "%" : "px";
  const value = typeof side.value === "number" ? side.value : Number(side.value);
  if (!Number.isFinite(value) || Math.abs(value) > MAX_LENGTH_MAGNITUDE) return "0" + unit;
  return `${value}${unit}`;
}
function cssDimension(side) {
  if (!side) return void 0;
  if (side.unit !== "px" && side.unit !== "%") return void 0;
  const value = typeof side.value === "number" ? side.value : Number(side.value);
  if (!Number.isFinite(value) || value <= 0 || value > MAX_LENGTH_MAGNITUDE) return void 0;
  return `${value}${side.unit}`;
}
function clampPercentToFull(length) {
  if (typeof length !== "string" || !length.endsWith("%")) return length;
  const n = Number(length.slice(0, -1));
  if (!Number.isFinite(n) || n <= 100) return length;
  return "100%";
}
function resolveSpacingStyle(spacing) {
  const margin = spacing?.margin;
  const padding = spacing?.padding;
  const style = {
    marginTop: cssLength(margin?.top, true),
    marginRight: cssLength(margin?.right, true),
    marginBottom: cssLength(margin?.bottom, true),
    marginLeft: cssLength(margin?.left, true),
    boxSizing: "border-box"
  };
  const hasPadding = SPACING_SIDES.some((name) => {
    const side = padding?.[name];
    const value = typeof side?.value === "number" ? side.value : Number(side?.value);
    return Number.isFinite(value) && value > 0;
  });
  if (hasPadding) {
    style.paddingTop = cssLength(padding?.top, false);
    style.paddingRight = cssLength(padding?.right, false);
    style.paddingBottom = cssLength(padding?.bottom, false);
    style.paddingLeft = cssLength(padding?.left, false);
  }
  const width = cssDimension(spacing?.width);
  if (width) style.width = width.endsWith("px") ? `min(${width}, 100%)` : width;
  const maxWidth = cssDimension(spacing?.maxWidth);
  if (maxWidth) style.maxWidth = maxWidth;
  return style;
}
var marginSideSchema = (unit) => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%", "auto"]).default(unit).describe("Unit for this side; auto centers")
}).default({ value: 0, unit });
var paddingSideSchema = () => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%"]).default("px").describe("Unit for this side")
}).default({ value: 0, unit: "px" });
var marginBoxSchema = (defaults) => z.object({
  top: marginSideSchema(defaults.top.unit),
  right: marginSideSchema(defaults.right.unit),
  bottom: marginSideSchema(defaults.bottom.unit),
  left: marginSideSchema(defaults.left.unit)
}).default(defaults).describe("Outer margin");
var paddingBoxSchema = () => z.object({
  top: paddingSideSchema(),
  right: paddingSideSchema(),
  bottom: paddingSideSchema(),
  left: paddingSideSchema()
}).default(DEFAULT_PADDING).describe("Inner padding");
var widthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Width length"),
  unit: z.enum(["px", "%", "auto"]).default(defaults.unit).describe("Width unit; auto leaves width unset")
}).default(defaults).describe("Width of the element");
var maxWidthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Maximum width length"),
  unit: z.enum(["px", "%", "none"]).default(defaults.unit).describe("Maximum width unit; none removes the constraint")
}).default(defaults).describe("Maximum width of the element");
var baseSpacingSchema = z.object({
  margin: marginBoxSchema(DEFAULT_MARGIN),
  padding: paddingBoxSchema(),
  width: widthSchema(DEFAULT_BASE_SPACING.width),
  maxWidth: maxWidthSchema(DEFAULT_BASE_SPACING.maxWidth)
}).default(DEFAULT_BASE_SPACING).describe("Margin, padding, width and maximum width of the widget");
function coerceSide(raw, extraUnit) {
  if (typeof raw === "number" || typeof raw === "string") {
    if (extraUnit && raw === extraUnit) return { value: 0, unit: extraUnit };
    const n2 = Number(raw);
    return Number.isFinite(n2) ? { value: n2, unit: "px" } : void 0;
  }
  if (!raw || typeof raw !== "object") return void 0;
  const side = raw;
  const n = Number(side.value);
  const unit = side.unit === "%" ? "%" : extraUnit && side.unit === extraUnit ? extraUnit : "px";
  return { value: Number.isFinite(n) ? n : 0, unit };
}
function coerceBox(raw, allowAuto) {
  if (!raw || typeof raw !== "object") return void 0;
  const box = raw;
  const out = {};
  for (const name of SPACING_SIDES) {
    const side = coerceSide(box[name], allowAuto ? "auto" : void 0);
    if (side) out[name] = side;
  }
  return out;
}
function coerceSpacingValue(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const input = raw;
  const out = { ...input };
  if ("margin" in input) {
    const margin = coerceBox(input.margin, true);
    if (margin) out.margin = margin;
    else delete out.margin;
  }
  if ("padding" in input) {
    const padding = coerceBox(input.padding, false);
    if (padding) out.padding = padding;
    else delete out.padding;
  }
  if ("width" in input) {
    const width = coerceSide(input.width, "auto");
    if (width) out.width = width;
    else delete out.width;
  }
  if ("maxWidth" in input) {
    const maxWidth = coerceSide(input.maxWidth, "none");
    if (maxWidth) out.maxWidth = maxWidth;
    else delete out.maxWidth;
  }
  return out;
}
function liftLegacySpacing(options) {
  if (!options || typeof options !== "object") return options;
  const hasSpacing = "spacing" in options;
  const hasLegacyWidth = "width" in options;
  const hasLegacyMargin = "margin" in options;
  const hasLegacyPadding = "padding" in options;
  if (!hasSpacing && !hasLegacyWidth && !hasLegacyMargin && !hasLegacyPadding) return options;
  const spacing = hasSpacing ? coerceSpacingValue(options.spacing) ?? {} : {};
  if (!("maxWidth" in spacing) && hasLegacyWidth) {
    const legacy = options.width;
    if (legacy && typeof legacy === "object" && "maxWidth" in legacy) {
      const n = Number(legacy.maxWidth);
      if (Number.isFinite(n)) {
        spacing.maxWidth = { value: n, unit: legacy.unit === "px" ? "px" : "%" };
      }
    }
  }
  if (!("margin" in spacing) && hasLegacyMargin) {
    const margin = coerceBox(options.margin, true);
    if (margin) spacing.margin = margin;
  }
  if (!("padding" in spacing) && hasLegacyPadding) {
    const padding = coerceBox(options.padding, false);
    if (padding) spacing.padding = padding;
  }
  return { ...options, spacing };
}
function mirrorLegacySpacing(settings) {
  const spacing = settings?.spacing;
  if (!spacing || typeof spacing !== "object" || Array.isArray(spacing)) return settings;
  const sp = spacing;
  let out = null;
  const ensure = () => out ??= { ...settings };
  const maxWidth = sp.maxWidth;
  if (maxWidth && typeof maxWidth === "object" && !Array.isArray(maxWidth)) {
    const side = maxWidth;
    if (side.unit === "none") {
      ensure().width = { maxWidth: 100, unit: "%" };
    } else {
      const n = Number(side.value);
      if (Number.isFinite(n) && (side.unit === "px" || side.unit === "%")) {
        ensure().width = { maxWidth: n, unit: side.unit };
      }
    }
  }
  for (const key of ["margin", "padding"]) {
    const box = sp[key];
    if (key in settings && box && typeof box === "object" && !Array.isArray(box)) {
      ensure()[key] = box;
    }
  }
  return out ?? settings;
}
var mirrorLegacyWidth = mirrorLegacySpacing;
function resolveWidgetSpacingStyle(options) {
  const lifted = liftLegacySpacing({ ...options ?? {} });
  const spacing = lifted.spacing ?? {};
  return resolveSpacingStyle({
    margin: spacing.margin ?? DEFAULT_MARGIN,
    padding: spacing.padding ?? DEFAULT_PADDING,
    width: spacing.width ?? DEFAULT_BASE_SPACING.width,
    maxWidth: spacing.maxWidth ?? DEFAULT_BASE_SPACING.maxWidth
  });
}

export { DEFAULT_BASE_SPACING, DEFAULT_MARGIN, DEFAULT_PADDING, DEFAULT_SLOT_SPACING, SPACING_SIDES, ZERO_MARGIN, baseSpacingSchema, clampPercentToFull, coerceSpacingValue, cssDimension, cssLength, liftLegacySpacing, marginBoxSchema, maxWidthSchema, mirrorLegacySpacing, mirrorLegacyWidth, paddingBoxSchema, resolveSpacingStyle, resolveWidgetSpacingStyle, widthSchema };
//# sourceMappingURL=spacing.js.map
//# sourceMappingURL=spacing.js.map